// Core domain types for Courage Rehearsal
// All timestamps are ms since epoch (Date.now() style)

export type WorkspaceId = string; // UUID v4

export type RuminationTheme =
  | 'replaying_exact_words'
  | 'imagining_judgment_faces'
  | 'reputation_long_term'
  | 'should_have_known'
  | 'perfect_prevention_loop'
  | 'other';

export type LoopFrequency =
  | 'rare'
  | 'monthly'
  | 'weekly'
  | 'few_per_week'
  | 'daily'
  | 'multiple_daily';

export type OutcomeType = 'question' | 'choice' | 'repair';

export interface EmotionalResidue {
  shame: number;      // 0-10
  anxiety: number;    // 0-10
  fatigue: number;    // 0-10  (burnout-relevant)
  self_doubt: number; // 0-10
}

export interface Rehearsal {
  id: string;                    // UUID
  workspace_id: WorkspaceId;
  created_at: number;
  duration_ms?: number;

  gratitude: string;
  scenario?: string;

  rumination_theme: RuminationTheme | string; // string allows "other:..."
  emotional_residue_before: EmotionalResidue;
  emotional_residue_after: EmotionalResidue;

  reappraisal_notes?: string;
  outcome_type: OutcomeType;
  outcome_text: string;

  tolerance_before: number;      // 1-10
  tolerance_after: number;       // 1-10  → primary signal for the curve
  reappraisal_success: number;   // 0-10
  repeat_loop_frequency: LoopFrequency;

  // Populated client-side for UI
  checkins?: Checkin[];
}

export interface Checkin {
  id: string;
  workspace_id: WorkspaceId;
  rehearsal_id: string;
  scheduled_for: number;
  completed_at?: number;
  days_since: number;

  residue_now?: EmotionalResidue;
  tolerance_now?: number;
  loop_frequency_now?: LoopFrequency;
  reflection?: string;
}

export interface WorkspaceConsent {
  consent_version: string;
  consent_granted_at?: number;
  retention_days: number;
  allow_server_storage: boolean;
  allow_analytics: boolean;
  allow_followups: boolean;
}

export interface Workspace {
  id: WorkspaceId;
  created_at: number;
  consent: WorkspaceConsent;
}

export interface CurvePoint {
  rehearsal_id: string;
  created_at: number;
  tolerance_before: number;
  tolerance_after: number;
  reappraisal_success: number;
  rumination_theme: string;
  outcome_type: OutcomeType;
  residue_reduction: number; // avg before - avg after
}

export interface RehearsalDraft {
  gratitude?: string;
  scenario?: string;
  rumination_theme?: RuminationTheme | string;
  emotional_residue_before?: Partial<EmotionalResidue>;
  reappraisal_notes?: string;
  outcome_type?: OutcomeType;
  outcome_text?: string;
  tolerance_before?: number;
  tolerance_after?: number;
  reappraisal_success?: number;
  repeat_loop_frequency?: LoopFrequency;
  emotional_residue_after?: Partial<EmotionalResidue>;
}

export const RUMINATION_LABELS: Record<RuminationTheme | 'other', string> = {
  replaying_exact_words: 'Replaying the exact words I said (or didn\'t say)',
  imagining_judgment_faces: 'Imagining colleagues\' faces or tone when they realized I was wrong',
  reputation_long_term: 'What this means for how competent / reliable I am seen as',
  should_have_known: 'The "I should have known / prepared better" loop',
  perfect_prevention_loop: 'Ruminating on how to make sure this can never happen again',
  other: 'Something else (describe below)',
};

export const FREQUENCY_LABELS: Record<LoopFrequency, string> = {
  rare: 'Rarely — only when something specific triggers it',
  monthly: 'A few times a month',
  weekly: 'Most weeks',
  few_per_week: 'Several times a week',
  daily: 'Most days',
  multiple_daily: 'Many times a day, it\'s hard to escape',
};

export const OUTCOME_LABELS: Record<OutcomeType, string> = {
  question: 'A better question I could ask (myself or others)',
  choice: 'A choice or micro-action I can take in the moment',
  repair: 'A repair I can offer or practice (if it already happened)',
};
